"use client";

/**
 * The tab bar, which replaces the shipped header nav.
 *
 * Three things move by putting navigation at the bottom. It is where the thumb
 * already is — the top corners of a 6-inch phone are the two hardest points to
 * reach one-handed, and that is where the portal currently puts four targets.
 * Every destination is visible at once, so nothing has to be remembered, and
 * Home stops being a tap on the wordmark: on the shipped portal Home is the
 * one item hidden on mobile (`mobile: false`), reachable only by pressing a
 * logo, which nothing teaches you is a link.
 *
 * Submit sits in the middle because it is the point of the product, and the
 * middle of the bottom edge is the easiest pixel on the screen to hit. It is
 * drawn as a filled tile rather than the raised circle the pattern usually
 * uses, and it keeps its label: a bare floating `+` is an idiom, and an idiom
 * is exactly what a first-time creator has not learned yet.
 *
 * No animation on selection. This is the most-pressed control in the app, and
 * by the frequency rule anything pressed dozens of times a session animates
 * into a delay. The press itself gets feedback — instantly, on pointer-down —
 * because that is the part that says the tap landed.
 */

import { ClipboardList, Home, MessageCircle, Plus, Video } from "lucide-react";
import { Rail } from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { CHROME, TABBAR_H } from "./chrome";
import type { Tab } from "./tabs";

const TABS: {
  id: Tab;
  /**
   * A key into the `shell` catalogue: the bar's labels are shorter than the
   * titles. Script review, Updates and Brands are not destinations on the bar.
   */
  label: `tabs.${Exclude<Tab, "script" | "updates" | "brands">}.short`;
  icon: typeof Home;
  /** The one action the whole surface exists for. */
  action?: boolean;
}[] = [
  // Submit sits in the centre slot, under the thumb and framed by the four
  // destinations, the way a phone's one primary action is placed.
  { id: "home", label: "tabs.home.short", icon: Home },
  { id: "requests", label: "tabs.requests.short", icon: ClipboardList },
  { id: "submit", label: "tabs.submit.short", icon: Plus, action: true },
  { id: "videos", label: "tabs.videos.short", icon: Video },
  { id: "inbox", label: "tabs.inbox.short", icon: MessageCircle },
];

export function TabBar({
  tab,
  onTab,
  unread,
  unseen,
}: {
  tab: Tab;
  onTab: (next: Tab) => void;
  /** Unread messages, shown on Inbox only when there are some. */
  unread: number;
  /**
   * Updates not yet read. A new brief is a reason to open the app, and
   * Updates lives inside Inbox, so it lights the same dot.
   */
  unseen: number;
}) {
  const t = useCreatorTranslations("shell");
  const badges: Partial<Record<Tab, boolean>> = {
    inbox: unread > 0 || unseen > 0,
  };

  return (
    <nav
      aria-label={t("nav.label")}
      // The inset is whatever the system owes this edge — the home indicator's
      // strip when installed, almost nothing inside Safari, where Safari's own
      // bar already covers it. A tab target that runs to the physical bottom
      // edge competes with the swipe-up gesture; one that pads for a gesture
      // bar that isn't reachable wastes the scarcest space on the screen.
      style={{
        height: `calc(${TABBAR_H}px + var(--one-inset, 24px))`,
        paddingBottom: "var(--one-inset, 24px)",
      }}
      className={cn(
        "absolute inset-x-0 bottom-0 z-20 flex items-start justify-around",
        "pt-nf-ds-1",
        // The clearance changes when Safari's bar collapses, so the tab bar
        // rides that change on the same curve and duration rather than
        // snapping while the browser's own bar glides.
        "transition-[height,padding-bottom] duration-[260ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none",
        CHROME,
      )}
    >
      {TABS.map((item) => {
        const active = tab === item.id;
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            aria-current={active ? "page" : undefined}
            onClick={() => onTab(item.id)}
            className={cn(
              "group flex min-h-[48px] w-[64px] flex-col items-center justify-center gap-nf-ds-1 rounded-[14px] outline-hidden",
              "transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.94] motion-reduce:transition-none motion-reduce:active:scale-100",
              "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
            )}
          >
            <span
              className={cn(
                "relative grid place-items-center",
                item.action
                  ? "h-[32px] w-[44px] rounded-[12px] bg-nf-ds-brand text-nf-ds-white"
                  : "h-[24px]",
              )}
            >
              <Icon
                className={cn(
                  item.action ? "size-[18px]" : "size-[22px]",
                  !item.action &&
                    (active ? "text-nf-ds-brand" : "text-muted-foreground"),
                )}
                strokeWidth={active && !item.action ? 2.4 : 2}
              />
              {badges[item.id] ? (
                // A dot, not a count. "3" on a tab is a number to clear; a dot
                // says there is something new, which is all it means here.
                <span
                  aria-hidden="true"
                  className="absolute -top-[2px] right-[1px] size-[9px] rounded-full bg-nf-ds-brand ring-2 ring-nf-ds-surface-ground"
                />
              ) : null}
            </span>
            <Rail
              muted={false}
              className={cn(
                active || item.action
                  ? "text-nf-ds-fg"
                  : "text-muted-foreground",
                active && "font-[700]",
              )}
            >
              {t(item.label)}
            </Rail>
            {badges[item.id] ? (
              <span className="sr-only">
                {unread > 0
                  ? t("nav.unread", { count: unread })
                  : t("nav.notReadYet", { count: unseen })}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
